/**
 * Central Notifications REST Client
 *
 * Calls Central notification endpoints DIRECTLY from the browser.
 * Same pattern as centralBootstrap.ts (direct Central URL, not backend proxy).
 *
 * Uses shared types and path constants from @levelup/central-notifications.
 * App-local React Query wiring is in hooks/useWorkspaceNotifications.ts.
 */

import {
  NOTIFICATION_INBOX_PATH,
  NOTIFICATION_UNREAD_COUNT_PATH,
  NOTIFICATION_MARK_READ_PATH,
} from '@levelup/central-notifications';
import type {
  InboxResponse,
  GetInboxParams,
  UnreadCountResponse,
  MarkReadResponse,
  MarkBulkReadParams,
  MarkBulkReadResponse,
} from '@levelup/central-notifications';
import { getAccessToken } from './centralAuth';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

/**
 * Authenticated fetch to Central notification endpoints.
 * Returns null on 401/403 (caller should handle re-auth).
 * Throws on network/unexpected errors.
 */
async function centralNotificationFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const centralBase = getCentralBase();
  if (!centralBase) {
    throw new Error('VITE_CENTRAL_URL not configured');
  }

  const url = new URL(path, centralBase).toString();

  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    throw new Error(`Central returned ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch workspace notification inbox from Central.
 */
export async function fetchWorkspaceInbox(
  params: Pick<GetInboxParams, 'workspaceId' | 'cursor' | 'limit'>,
): Promise<InboxResponse> {
  const searchParams = new URLSearchParams({
    workspaceId: params.workspaceId,
  });
  if (params.cursor) searchParams.set('cursor', params.cursor);
  if (params.limit) searchParams.set('limit', String(params.limit));

  return centralNotificationFetch<InboxResponse>(
    `${NOTIFICATION_INBOX_PATH}?${searchParams.toString()}`,
  );
}

/**
 * Fetch workspace unread count from Central.
 */
export async function fetchWorkspaceUnreadCount(
  workspaceId: string,
): Promise<UnreadCountResponse> {
  const searchParams = new URLSearchParams({ workspaceId });
  return centralNotificationFetch<UnreadCountResponse>(
    `${NOTIFICATION_UNREAD_COUNT_PATH}?${searchParams.toString()}`,
  );
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationRead(
  notificationId: string,
): Promise<MarkReadResponse> {
  return centralNotificationFetch<MarkReadResponse>(
    `${NOTIFICATION_MARK_READ_PATH}/${notificationId}`,
    { method: 'PATCH' },
  );
}

/**
 * Mark multiple notifications as read (bulk).
 */
export async function markBulkRead(
  params: MarkBulkReadParams,
): Promise<MarkBulkReadResponse> {
  return centralNotificationFetch<MarkBulkReadResponse>(
    NOTIFICATION_MARK_READ_PATH,
    {
      method: 'PATCH',
      body: JSON.stringify(params),
    },
  );
}
